import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { HydrationError } from './types.ts';

interface LoggingConfig {
	selectors?: boolean;
	errors?: boolean;
	stack?: boolean;
	stackLength?: number;
}

interface HydrationErrorAccumulator {
	[key: string]: string | number;
}

const getElementSelector = (element: HTMLElement | undefined) => {
	if (!element || !element.tagName) {
		return 'n/a';
	}

	const selector = `${element.tagName.toLowerCase()}.${element.className?.split(' ').join('.')}`;

	if (element.dataset?.testid) {
		return `${selector}[data-testid="${element.dataset?.testid}"]`;
	}

	return selector;
};

export const convertHydrationErrors = (hydrationErrors: HydrationError[]) => {
	let loggingConfig: LoggingConfig = {};
	try {
		loggingConfig = JSON.parse(
			StatsigConfigurations.getString('confluence_hydration_error_logging', '{}'),
		);
	} catch {
		// no-op, if an error occurs here, there's really nowhere to submit it
	}

	return hydrationErrors.reduce((accumulator: HydrationErrorAccumulator, error) => {
		if (!error.type || !error.msg) return accumulator;

		// Log selectors for mismatched hydration errors. The presense of parentNode or parent
		// depend on actual hydration error / patch used.
		const finalParent = error.parent || error.parentNode;
		if (loggingConfig.selectors && (finalParent || error.child)) {
			if (!accumulator.selectors) {
				accumulator.selectors = '';
			}
			const parent = finalParent ? getElementSelector(finalParent) : 'n/a';
			const child = error.child ? getElementSelector(error.child) : 'n/a';

			accumulator.selectors += `${parent}->${child}; `;
		}

		// For UNSAFE_TEXT_MISMATCH errors, only track the count
		// Don't log the actual text content as it contains sensitive data
		// Similarly, UNSAFE_CLIENT_MISMATCH and UNSAFE_SERVER_MISMATCH contain user content in text nodes
		if (loggingConfig.errors) {
			if (error.type === 'UNSAFE_TEXT_MISMATCH') {
				accumulator.unsafeTextMismatches = ((accumulator.unsafeTextMismatches as number) || 0) + 1;
			} else if (error.type === 'UNSAFE_CLIENT_MISMATCH') {
				accumulator.unsafeClientMismatches =
					((accumulator.unsafeClientMismatches as number) || 0) + 1;
			} else if (error.type === 'UNSAFE_SERVER_MISMATCH') {
				accumulator.unsafeServerMismatches =
					((accumulator.unsafeServerMismatches as number) || 0) + 1;
			} else {
				if (!accumulator[error.type]) {
					accumulator[error.type] = '';
				}
				accumulator[error.type] += (accumulator[error.type] ? ' ; ' : '') + error.msg;
			}
		}

		// Add error stack
		if (loggingConfig.stack && error.stack) {
			if (!accumulator.stack) {
				accumulator.stack = '';
			}
			accumulator.stack += `Stack: ${error.stack} \n`;
		}
		return accumulator;
	}, {});
};
